import moment from 'moment'
import React from 'react'

import Badge from 'components/common/Badge'
import { Column } from 'components/common/Grid'
import Popover from 'components/common/Popover'
import PopoverMenu from 'components/common/PopoverMenu'
import Text from 'components/common/Text'
import TextButton from 'components/common/TextButton'
import Icon from 'components/Icon'
import theme from 'theme'
import { ApiKey } from 'types/api/api-key'
import { getColorForApiKeyState } from 'utils/colors'

import { apiKeysColumns } from './ApiKeysTable'
import PreviewApiKeyPopover from './PreviewApiKeyPopover'
import ReinstateApiKeyButton from './ReinstateApiKeyButton'
import RevokeApiKeyModal from './RevokeApiKeyModal'

const ApiKeyRow: React.FC<{
  apiKey: ApiKey
  doRefresh: () => void
  apiKeyLimitReached: boolean
}> = ({ apiKey, doRefresh, apiKeyLimitReached }) => {
  const [isRevokeModalOpen, setIsRevokeModalOpen] = React.useState(false)
  const isKeyActive = apiKey.state === 'ACTIVE' // TODO can i pull this from ApiKeyStatus somehow?
  const isMenuHidden = !isKeyActive && apiKeyLimitReached

  return (
    <>
      <RevokeApiKeyModal
        apiKey={apiKey}
        fetchApiKeys={doRefresh}
        isOpen={isRevokeModalOpen}
        setOpenState={setIsRevokeModalOpen}
      />
      <Column span={apiKeysColumns.status.span}>
        <Badge
          css={{ background: getColorForApiKeyState(apiKey.state) }}
          data-test-api-key-state={apiKey.identifier}
        >
          {apiKey.state}
        </Badge>
      </Column>
      <Column
        css={{ display: 'flex', flexDirection: 'column' }}
        span={apiKeysColumns.name.span}
      >
        <div css={{ display: 'flex', flexDirection: 'row' }}>
          <Text as="h4">{apiKey.identifier}</Text>
          <PreviewApiKeyPopover
            apiKey={apiKey.value}
            css={{
              flexShrink: 0,
              width: theme.space[3],
              fontSize: theme.fontSizes[2],
              marginLeft: theme.space[2]
            }}
          />
        </div>
        {apiKey.description && (
          <Text as="small" css={{ marginTop: theme.space[1] }}>
            {apiKey.description}
          </Text>
        )}
      </Column>
      <Column span={apiKeysColumns.created.span}>
        <Text as="small">
          {moment(apiKey.created_at).format('M/D/YYYY h:mm a')}
        </Text>
      </Column>
      <Column span={apiKeysColumns.lastUsed.span}>
        <Text as="small">
          {apiKey.last_used_at
            ? moment(apiKey.last_used_at).format('M/D/YYYY h:mm a')
            : 'Never'}
        </Text>
      </Column>
      <Column span={apiKeysColumns.actions.span}>
        <Popover alignPopover={Popover.ALIGN_RIGHT}>
          {({ getTargetProps, getPopoverProps }) => (
            <>
              <Icon
                data-api-key-menu={apiKey.identifier}
                icon="solidEllipsis"
                {...getTargetProps({
                  css: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: theme.space[2],
                    width: 48,
                    height: theme.space[4],
                    cursor: 'pointer',
                    color: theme.colors.steel[1],
                    borderRadius: theme.borderRadius[0],

                    '&:hover': {
                      color: theme.colors.steel[2]
                    }
                  }
                })}
              />
              <PopoverMenu getPopoverProps={getPopoverProps}>
                {isKeyActive ? (
                  <TextButton
                    destructive
                    data-api-key-action-revoke={apiKey.identifier}
                    onClick={() => setIsRevokeModalOpen(true)}
                  >
                    Revoke Key
                  </TextButton>
                ) : (
                  <ReinstateApiKeyButton
                    disabled={isMenuHidden}
                    fetchApiKeys={doRefresh}
                    identifier={apiKey.identifier}
                  />
                )}
              </PopoverMenu>
            </>
          )}
        </Popover>
      </Column>
    </>
  )
}

export default ApiKeyRow
